import type { DashboardSummaryResponse } from '../components/dashboard-widgets/types';
import { clearCommonDataCache, commonDataService } from './commonDataService';
import { DedupedGet } from './dedupedFetch';

/**
 * Dedupe + short-TTL caching layer for the list endpoints that power the
 * Dashboard. Same motivation as {@link ./projectsDataService}: StrictMode
 * double-invokes the Dashboard's mount effect in dev, and a transient remount
 * (e.g. auth-state flipping, role/config load) can do the same in prod. Without
 * coordination each endpoint fires twice.
 *
 * Several entries are **aliases** of {@link commonDataService} so their cache
 * is shared across pages that hit the same byte-identical URL (Dashboard,
 * Reports, `BaseTimeline`, etc.). Page-specific query-string variants (e.g.
 * `/allocations?limit=1000`) stay on their own service.
 *
 * `teamUtilisation` is a URL-keyed cache because the Dashboard and Reports each
 * hit this endpoint with their own selected range; matching ranges share a
 * slot, mismatched ones don't.
 */
export const dashboardDataService = {
  config: new DedupedGet<any>('/dashboard-config'),
  projectStagesAll: new DedupedGet<any[]>(
    '/project-stages?includeInactive=true',
  ),
  // Aliased into commonDataService so Dashboard + Reports (and any future page
  // hitting the identical URL) share one cache slot.
  jobTitleDepartmentMappings: commonDataService.jobTitleDepartmentMappings,
  employees: commonDataService.employeesNoAbsences,
  teamUtilisation: commonDataService.teamUtilisation,
  summary: new DedupedGet<DashboardSummaryResponse>('/dashboard/summary'),
};

/**
 * Clear every cache managed by this service. Wired up from the Dashboard's
 * Refresh button and the post-config-save refetch so explicit user action
 * always hits the wire.
 *
 * Also clears `commonDataService` so shared caches (projects, employees,
 * allocations, job-title/department mappings, team utilisation, staffing
 * requirements) are wiped on a user-driven refresh — otherwise a Dashboard
 * Refresh would silently serve cached data from another page.
 */
export function clearDashboardDataCache(): void {
  dashboardDataService.config.clear();
  dashboardDataService.projectStagesAll.clear();
  dashboardDataService.summary.clear();
  clearCommonDataCache();
}
